/* Thermal view simulator: palettes, field of view and digital zoom.
   Media are real thermal stills (brand supplied, white hot). Everything else is computed from the
   device's sensor resolution, pixel pitch, lens focal length, base magnification and screen resolution.
   Pipeline: scene -> sensor (area average) -> device screen (smooth upscale, as the device does) -> page. Used by snippets/htn-thermal-demo.liquid
   (one device) and sections/htn-thermal-compare.liquid (side by side). */
(function () {
  'use strict';
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  // Typical adult sizes in metres (h = height at the shoulder, l = body length without tail).
  // The animal is drawn so its silhouette is h tall at the chosen distance; Johnson criteria use the same height.
  var ANIMALS = {
    // bh: height of the whole silhouette in the still (ears up), when it is taller than the shoulder
    rabbit: { name: 'Rabbit', full: 'European rabbit', h: 0.2, l: 0.4, bh: 0.36 },
    fox: { name: 'Fox', full: 'Red fox', h: 0.38, l: 0.7, bh: 0.5 },
    pig: { name: 'Pig', full: 'Feral pig', h: 0.7, l: 1.4 },
    deer: { name: 'Deer', full: 'Fallow deer', h: 0.9, l: 1.5 }
  };
  function sizeText(a) { return a.full + ': about ' + a.h.toFixed(2) + ' m at the shoulder, ' + a.l.toFixed(1) + ' m long'; }
  // Johnson criteria (sensor pixels across the shoulder height), worded for people rather than pixels.
  var JOHNSON = [
    { px: 13, label: 'Identify', say: function (a) { return 'Clearly a ' + a; }, to: function (a) { return 'See it clearly to'; } },
    { px: 8, label: 'Recognise', say: function (a) { return 'Looks like a ' + a; }, to: function (a) { return 'Know it\'s a ' + a + ' to'; } },
    { px: 2, label: 'Detect', say: function () { return 'Something warm'; }, to: function () { return 'Spot it to'; } }
  ];
  var DISTS = [50, 100, 200, 400, 800];
  // Sliders: distance 10 to 1000 m and digital zoom on top of the base magnification, both on a log scale.
  var DMIN = 10, DMAX = 1000;
  function distOf(v) { var d = DMIN * Math.pow(DMAX / DMIN, v / 1000); return d < 100 ? Math.round(d) : d < 400 ? Math.round(d / 5) * 5 : Math.round(d / 10) * 10; }
  function zoomOf(v) { return Math.round(Math.pow(2, v / 100) * 10) / 10; }
  function zfmt(z) { z = Math.round(z * 10) / 10; return (z % 1 ? z.toFixed(1) : z) + '×'; }
  // Digital zoom range: up to the device's listed maximum (e.g. 3–24× is 8× digital), otherwise 8×.
  function zmax(dev) { return dev.m && dev.mx > dev.m ? Math.min(16, Math.max(1, dev.mx / dev.m)) : 8; }
  // What the eye sees: base magnification times digital zoom; without a listed base it is just the zoom.
  function magText(dev, z) { return dev.m ? zfmt(dev.m * z) : zfmt(z) + ' zoom'; }
  var QUAL = ['Sharp', 'Slightly soft', 'Soft', 'Blurry'];
  function quality(z) { return QUAL[Math.min(3, Math.max(0, Math.round(Math.log(z) / Math.LN2)))]; }
  var ZOOMS = [1, 2, 4, 8];
  var ZOOM_AT = [0.3, 0.6]; // zoom centre in the scene (the near pig)

  function verdict(px, name) {
    for (var i = 0; i < JOHNSON.length; i++) if (px >= JOHNSON[i].px) return JOHNSON[i].say(name);
    return 'Nothing to see';
  }

  // ---- palettes: 256-entry RGB lookup tables applied to the white-hot source ----
  function ramp(stops) {
    var t = new Uint8Array(768);
    for (var i = 0; i < 256; i++) {
      var x = i / 255, k = 0;
      while (k < stops.length - 2 && x > stops[k + 1][0]) k++;
      var a = stops[k], b = stops[k + 1], f = (x - a[0]) / ((b[0] - a[0]) || 1);
      f = Math.max(0, Math.min(1, f));
      for (var c = 0; c < 3; c++) t[i * 3 + c] = Math.round(a[1][c] + (b[1][c] - a[1][c]) * f);
    }
    return t;
  }
  var LUT = {
    whitehot: ramp([[0, [0, 0, 0]], [1, [255, 255, 255]]]),
    blackhot: ramp([[0, [255, 255, 255]], [1, [0, 0, 0]]]),
    redhot: ramp([[0, [0, 0, 0]], [0.62, [168, 168, 168]], [0.72, [200, 60, 40]], [1, [255, 40, 20]]]),
    colour: ramp([[0, [20, 15, 51]], [0.22, [75, 22, 112]], [0.45, [179, 38, 74]], [0.66, [240, 86, 28]], [0.84, [255, 166, 43]], [1, [255, 241, 201]]])
  };

  // ---- image helpers ----
  var cache = {};
  function load(src) {
    if (!src) return Promise.reject();
    if (!cache[src]) cache[src] = new Promise(function (ok, no) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () { ok(im); };
      im.onerror = no;
      im.src = src;
    });
    return cache[src];
  }
  function canvas(w, h) { var c = document.createElement('canvas'); c.width = Math.max(1, w); c.height = Math.max(1, h); return c; }
  // Area-average downscale (how a sensor integrates light into each pixel). Steps down by halves to avoid aliasing.
  function shrink(src, w, h) {
    var cur = src, cw = src.naturalWidth || src.width, ch = src.naturalHeight || src.height;
    while (cw / 2 > w && ch / 2 > h) {
      var n = canvas(Math.round(cw / 2), Math.round(ch / 2)), x = n.getContext('2d');
      x.imageSmoothingQuality = 'high'; x.drawImage(cur, 0, 0, n.width, n.height); cur = n; cw = n.width; ch = n.height;
    }
    var o = canvas(w, h), ox = o.getContext('2d');
    ox.imageSmoothingQuality = 'high'; ox.drawImage(cur, 0, 0, w, h);
    return o;
  }
  // The device's screen: what reaches the eyepiece is a grid of sensor pixels (or the screen's own pixels, if the
  // screen has fewer). Returns that grid; present() then draws each cell as a soft square, so a 384 sensor looks
  // coarser than a 640 or a 1280 at the same magnification. Screens not listed are taken as 1024x768.
  function screen(sensor, dev, sx, sy, sw, sh) {
    sx = Math.round(sx); sy = Math.round(sy); sw = Math.max(1, Math.round(sw)); sh = Math.max(1, Math.round(sh));
    var dh = dev.dh || 768, gh = Math.min(sh, dh), gw = Math.max(1, Math.round(gh * sw / sh)), c = canvas(gw, gh), x = c.getContext('2d');
    x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
    x.drawImage(sensor, sx, sy, sw, sh, 0, 0, gw, gh);
    enhance(x, gw, gh, 0.7);
    return c;
  }
  // Detail enhancement, as thermal devices do in their image processing: an unsharp mask one grid cell wide, so
  // edges are crisper but no detail finer than the device's own pixels appears. Also used at page resolution.
  function enhance(x, W, H, amt) {
    var id = x.getImageData(0, 0, W, H), d = id.data, t = new Float32Array(W * H), u = new Float32Array(W * H), i, j, q;
    for (j = 0; j < H; j++) for (i = 0; i < W; i++) { q = j * W + i; t[q] = (d[(q - (i > 0)) * 4] + 2 * d[q * 4] + d[(q + (i < W - 1)) * 4]) / 4; }
    for (j = 0; j < H; j++) for (i = 0; i < W; i++) { q = j * W + i; u[q] = (t[q - (j > 0) * W] + 2 * t[q] + t[q + (j < H - 1) * W]) / 4; }
    for (q = 0; q < W * H; q++) d[q * 4] = d[q * 4 + 1] = d[q * 4 + 2] = d[q * 4] + amt * (d[q * 4] - u[q]);
    x.putImageData(id, 0, 0);
  }
  // Draws a grid onto a page canvas at device resolution, then applies the palette. Each grid cell is enlarged as
  // a block first, then smoothed once, like a screen pixel seen through an eyepiece. When a cell is under two page
  // pixels (640 and 1280 sensors on smaller canvases) the scaling softens it, so the result is sharpened again.
  function present(out, src, pal, sx, sy, sw, sh) {
    var r = out.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 3);
    var W = Math.max(1, Math.round(r.width * dpr)), H = Math.max(1, Math.round(r.height * dpr));
    if (out.width !== W || out.height !== H) { out.width = W; out.height = H; }
    sx = sx || 0; sy = sy || 0; sw = sw || src.width; sh = sh || src.height;
    var n = Math.floor(H / sh), x = out.getContext('2d');
    if (n >= 2) {
      var b = canvas(Math.round(sw * n), Math.round(sh * n)), bx = b.getContext('2d');
      bx.imageSmoothingEnabled = false; bx.drawImage(src, sx, sy, sw, sh, 0, 0, b.width, b.height);
      src = b; sx = 0; sy = 0; sw = b.width; sh = b.height;
    }
    x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
    x.drawImage(src, sx, sy, sw, sh, 0, 0, W, H);
    if (n < 2) enhance(x, W, H, 0.6);
    tint(x, W, H, pal);
  }
  function tint(x, W, H, pal) {
    var lut = LUT[pal] || LUT.whitehot, d = x.getImageData(0, 0, W, H), p = d.data;
    for (var i = 0; i < p.length; i += 4) {
      var v = p[i] * 3; p[i] = lut[v]; p[i + 1] = lut[v + 1]; p[i + 2] = lut[v + 2];
    }
    x.putImageData(d, 0, 0);
  }
  // Cuts the warm animal out of its still: background tone from the image edge, alpha from how much warmer
  // each pixel is, and the silhouette's bounding box so it can be scaled to the animal's real height.
  var TONE = 60, BODY = [140, 215]; // ground grey level and the body's 10th-90th percentile range
  function cutout(im) {
    if (im._cut) return im._cut;
    var W = im.naturalWidth, H = im.naturalHeight, c = canvas(W, H), x = c.getContext('2d');
    x.drawImage(im, 0, 0);
    var id = x.getImageData(0, 0, W, H), d = id.data, s = 0, n = 0, X, Y;
    for (Y = 0; Y < H; Y++) for (X = 0; X < W; X++) if (X < 3 || Y < 3 || X >= W - 3 || Y >= H - 3) { s += d[(Y * W + X) * 4]; n++; }
    var bg = s / n, rows = new Array(H).fill(0), cols = new Array(W).fill(0);
    for (Y = 0; Y < H; Y++) for (X = 0; X < W; X++) {
      var i = (Y * W + X) * 4;
      d[i + 3] = Math.round(Math.max(0, Math.min(1, (d[i] - bg - 18) / 40)) * 255);
    }
    // Other warm things cut off at the edge of the still (another animal's flank) are dropped: every warm patch
    // touching the border is removed unless it is the largest one, which is the animal itself.
    var lab = new Int32Array(W * H), sizes = [0], edge = [false], stack = [], k, q, best = 0;
    for (q = 0; q < W * H; q++) if (d[q * 4 + 3] > 12 && !lab[q]) {
      k = sizes.length; sizes.push(0); edge.push(false); lab[q] = k; stack.push(q);
      while (stack.length) {
        var p = stack.pop(), px = p % W, py = (p - px) / W; sizes[k]++;
        if (px === 0 || py === 0 || px === W - 1 || py === H - 1) edge[k] = true;
        [px > 0 ? p - 1 : -1, px < W - 1 ? p + 1 : -1, py > 0 ? p - W : -1, py < H - 1 ? p + W : -1].forEach(function (nq) {
          if (nq >= 0 && !lab[nq] && d[nq * 4 + 3] > 12) { lab[nq] = k; stack.push(nq); }
        });
      }
      if (sizes[k] > sizes[best]) best = k;
    }
    for (q = 0; q < W * H; q++) if (lab[q] && lab[q] !== best && edge[lab[q]]) d[q * 4 + 3] = 0;
    for (Y = 0; Y < H; Y++) for (X = 0; X < W; X++) if (d[(Y * W + X) * 4 + 3] > 127) { rows[Y]++; cols[X]++; }
    // Same contrast for every animal: map the body's 10th-90th percentile warmth to one fixed range over one
    // fixed ground tone, so brand stills and converted photos read alike.
    var vals = [];
    for (i = 0; i < d.length; i += 4) if (d[i + 3] > 127) vals.push(d[i]);
    vals.sort(function (p, q) { return p - q; });
    var p10 = vals[Math.floor(vals.length * 0.1)] || 0, p90 = vals[Math.floor(vals.length * 0.9)] || 255, g = (BODY[1] - BODY[0]) / Math.max(1, p90 - p10);
    for (i = 0; i < d.length; i += 4) d[i] = d[i + 1] = d[i + 2] = Math.max(TONE, Math.min(255, BODY[0] + (d[i] - p10) * g));
    x.putImageData(id, 0, 0);
    function span(arr, t) { var i = 0, j = arr.length - 1; while (i < j && arr[i] < t) i++; while (j > i && arr[j] < t) j--; return [i, j + 1]; }
    var ry = span(rows, Math.max(2, W * 0.03)), rx = span(cols, Math.max(2, H * 0.03));
    im._cut = { c: c, tone: TONE, x0: rx[0], x1: rx[1], y0: ry[0], y1: ry[1] };
    return im._cut;
  }

  // ---- optics ----
  function optics(dev) {
    var ifov = dev.pitch / dev.focal / 1000; // radians per pixel
    return {
      ifov: ifov,
      hfov: 2 * Math.atan(dev.w * dev.pitch / 2000 / dev.focal) * 180 / Math.PI,
      vfov: 2 * Math.atan(dev.h * dev.pitch / 2000 / dev.focal) * 180 / Math.PI,
      px: function (m, d) { return m / (d * ifov); },
      span: function (d) { return dev.w * ifov * d; },
      range: function (m, px) { return m / (px * ifov); }
    };
  }

  // Integer hash to [0,1), and smooth value noise on a 1-unit lattice.
  function hash(i, j) { var h = (Math.imul(i, 374761393) + Math.imul(j, 668265263)) | 0; h = Math.imul(h ^ (h >>> 13), 1274126177); return ((h ^ (h >>> 16)) >>> 0) / 4294967296; }
  function vnoise(X, Y) {
    var i = Math.floor(X), j = Math.floor(Y), u = X - i, v = Y - j;
    u = u * u * (3 - 2 * u); v = v * v * (3 - 2 * v);
    var a = hash(i, j), b = hash(i + 1, j), c = hash(i, j + 1), d = hash(i + 1, j + 1);
    return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
  }
  var GROUND = [[0.35, 4], [1.4, 6], [6, 8]]; // [wavelength m, grey levels]: grass, scrub, terrain
  // Ground behind the animal, fixed in metres around it so every device sees the same patch. Detail finer than
  // the device can resolve is averaged out, so a coarser sensor shows a flatter, blockier background.
  function ground(x, dev, dist, tone) {
    var m = dev.pitch / dev.focal / 1000 * dist, tw = Math.max(2, Math.min(dev.w, Math.ceil(dev.w * m / 0.08))), th = Math.max(2, Math.round(tw * dev.h / dev.w));
    var cm = dev.w * m / tw, t = canvas(tw, th), tx = t.getContext('2d'), id = tx.createImageData(tw, th), d = id.data;
    var oct = GROUND.filter(function (g) { return g[0] >= 2.5 * Math.max(m, cm); });
    for (var j = 0; j < th; j++) for (var i = 0; i < tw; i++) {
      var X = (i - tw / 2 + 0.5) * cm, Y = (j - th / 2 + 0.5) * cm, v = tone;
      for (var k = 0; k < oct.length; k++) { var a = 0.6 + k * 1.1, ca = Math.cos(a), sa = Math.sin(a); v += (vnoise((X * ca - Y * sa) / oct[k][0] + 50 * k, (X * sa + Y * ca) / oct[k][0]) - 0.5) * 2 * oct[k][1]; } // rotated so the noise has no grid of its own
      var q = (j * tw + i) * 4; d[q] = d[q + 1] = d[q + 2] = v; d[q + 3] = 255;
    }
    tx.putImageData(id, 0, 0);
    x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
    x.drawImage(t, 0, 0, dev.w, dev.h);
  }
  // Lens and detector blur: each sensor pixel also picks up a little of its neighbours ([1 4 1]/6 each way), so
  // fine detail fades at the sensor's own resolution, not the page's.
  function soften(x, dev) {
    var W = dev.w, H = dev.h, id = x.getImageData(0, 0, W, H), d = id.data, t = new Float32Array(W * H), i, j, q;
    for (j = 0; j < H; j++) for (i = 0; i < W; i++) { q = j * W + i; t[q] = (d[(q - (i > 0)) * 4] + 4 * d[q * 4] + d[(q + (i < W - 1)) * 4]) / 6; }
    for (j = 0; j < H; j++) for (i = 0; i < W; i++) { q = j * W + i; d[q * 4] = d[q * 4 + 1] = d[q * 4 + 2] = (t[q - (j > 0) * W] + 4 * t[q] + t[q + (j < H - 1) * W]) / 6; }
    x.putImageData(id, 0, 0);
  }
  // Sensor noise: every sensor pixel reads slightly differently, so the grain shows how many pixels there are.
  function grain(x, dev) {
    var id = x.getImageData(0, 0, dev.w, dev.h), d = id.data;
    for (var j = 0; j < dev.h; j++) for (var i = 0; i < dev.w; i++) {
      var q = (j * dev.w + i) * 4, v = d[q] + (hash(i + 7, j + 3) - 0.5) * 8;
      d[q] = d[q + 1] = d[q + 2] = v;
    }
    x.putImageData(id, 0, 0);
  }

  // Renders the sensor frame (dev.w x dev.h) with the animal's silhouette at its true pixel height, centred.
  function frameFor(dev, img, animalH, dist) {
    var cut = cutout(img), o = optics(dev), px = o.px(animalH, dist), f = canvas(dev.w, dev.h), x = f.getContext('2d');
    ground(x, dev, dist, cut.tone);
    var k = px / (cut.y1 - cut.y0), w = Math.max(1, Math.round(cut.c.width * k)), h = Math.max(1, Math.round(cut.c.height * k));
    var ax = dev.w / 2 - (cut.x0 + cut.x1) / 2 * k, ay = dev.h / 2 - (cut.y0 + cut.y1) / 2 * k;
    if (h <= dev.h * 4) x.drawImage(shrink(cut.c, w, h), Math.round(ax), Math.round(ay));
    soften(x, dev); grain(x, dev);
    return { c: f, px: px, box: [ax + cut.x0 * k, ay + cut.y0 * k, (cut.x1 - cut.x0) * k, px], span: o.span(dist) };
  }

  // Through-the-eyepiece view at base magnification times digital zoom, centred on the animal. The eyepiece
  // shows the whole screen at base magnification; digital zoom crops the sensor and enlarges it, adding no detail.
  function scope(out, f, dev, zoom, pal) {
    var side = dev.h / zoom, cx = f.box[0] + f.box[2] / 2, cy = f.box[1] + f.box[3] / 2;
    var sx = Math.min(dev.w - side, Math.max(0, cx - side / 2)), sy = Math.min(dev.h - side, Math.max(0, cy - side / 2));
    present(out, screen(f.c, dev, sx, sy, side, side), pal);
    var x = out.getContext('2d'), W = out.width, r = W / 2, k = W / 400;
    x.save();
    x.globalCompositeOperation = 'destination-in';
    x.beginPath(); x.arc(r, r, r - 1, 0, 7); x.fill();
    x.restore();
    if (dev.ret) { // rifle scopes only; monoculars, binoculars and clip-ons have no reticle of their own
      x.strokeStyle = 'rgba(255,255,255,.75)'; x.lineWidth = Math.max(1, k);
      x.beginPath();
      x.moveTo(r, r * 0.08); x.lineTo(r, r * 0.8); x.moveTo(r, r * 1.2); x.lineTo(r, r * 1.92);
      x.moveTo(r * 0.08, r); x.lineTo(r * 0.8, r); x.moveTo(r * 1.2, r); x.lineTo(r * 1.92, r);
      x.stroke();
    }
    x.strokeStyle = '#1b1b1b'; x.lineWidth = 8 * k;
    x.beginPath(); x.arc(r, r, r - 4 * k, 0, 7); x.stroke();
    return side;
  }

  function mp(n) { return n >= 1e6 ? (n / 1e6).toFixed(1) + ' MP' : Math.round(n / 1e3) + 'k'; }
  function fmt(n) { return n >= 100 ? Math.round(n) : n >= 10 ? n.toFixed(0) : n.toFixed(1); }

  function near(a, b) { return Math.abs(Math.log(a / b)) < 0.18; }
  // Distance and magnification sliders, shared by the PDP demo and the compare page.
  function sliders(el, s, dev) {
    var r = el.querySelector('[data-tv-dist]'), m = el.querySelector('[data-tv-mag]');
    if (r) r.addEventListener('input', function () { s.dist = distOf(+r.value); s.draw(); });
    if (m) m.addEventListener('input', function () { s.fz = zoomOf(+m.value); s.draw(); });
    // single device: the slider runs from base to maximum magnification, with ticks in real magnifications
    var t = el.querySelector('[data-tv-mag-ticks]');
    if (m && dev) {
      var zm = zmax(dev), top = Math.round(Math.log(zm) / Math.LN2 * 100), z, h = '';
      m.max = top;
      for (z = 1; z < zm * 0.99; z *= 2) h += '<i style="left:' + (Math.log(z) / Math.log(zm) * 100).toFixed(1) + '%">' + magText(dev, z).replace(' zoom', '') + '</i>';
      if (t) t.innerHTML = h + '<i style="left:100%">' + magText(dev, zm).replace(' zoom', '') + '</i>';
    }
  }
  function labels(el, s, dev) {
    var a = ANIMALS[s.animal];
    $$('[data-tv-dist-v]', el).forEach(function (n) { n.textContent = s.dist + ' m'; });
    $$('[data-tv-mag-v], [data-tv-r="fz"]', el).forEach(function (n) { n.textContent = dev ? magText(dev, s.fz) : zfmt(s.fz); });
    $$('[data-tv-r="size"]', el).forEach(function (n) { n.textContent = sizeText(a); });
  }

  // ================= single device (PDP) =================
  function Demo(el) {
    this.el = el;
    this.dev = JSON.parse(el.getAttribute('data-dev'));
    this.src = JSON.parse(el.getAttribute('data-src'));
    this.pal = 'whitehot'; this.animal = 'pig'; this.dist = 200; this.zoom = 1; this.fz = 1;
    this.tab = (el.querySelector('[data-tv-tab][aria-pressed="true"]') || {}).getAttribute ? el.querySelector('[data-tv-tab][aria-pressed="true"]').getAttribute('data-tv-tab') : 'fov';
    this.bind();
  }
  Demo.prototype.bind = function () {
    var s = this, el = this.el;
    $$('[data-tv-tab]', el).forEach(function (b) { b.addEventListener('click', function () { s.tab = b.getAttribute('data-tv-tab'); s.draw(); }); });
    $$('[data-tv-pal]', el).forEach(function (b) { b.addEventListener('click', function () { s.pal = b.getAttribute('data-tv-pal'); s.draw(); }); });
    $$('[data-tv-animal]', el).forEach(function (b) { b.addEventListener('click', function () { s.animal = b.getAttribute('data-tv-animal'); s.draw(); }); });
    $$('[data-tv-zoom]', el).forEach(function (b) { b.addEventListener('click', function () { s.zoom = +b.getAttribute('data-tv-zoom'); s.draw(); }); });
    sliders(el, s, this.dev);
    $$('[data-tv-zoom]', el).forEach(function (b) { var z = +b.getAttribute('data-tv-zoom'); b.hidden = z > zmax(s.dev) * 1.01; b.textContent = magText(s.dev, z); });
    var raf; window.addEventListener('resize', function () { cancelAnimationFrame(raf); raf = requestAnimationFrame(function () { s.draw(); }); });
    this.draw();
  };
  Demo.prototype.press = function (attr, val) {
    $$('[' + attr + ']', this.el).forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute(attr) === String(val))); });
  };
  Demo.prototype.draw = function () {
    var s = this, el = this.el;
    this.press('data-tv-tab', this.tab); this.press('data-tv-pal', this.pal); this.press('data-tv-animal', this.animal); this.press('data-tv-zoom', this.zoom);
    $$('[data-tv-panel]', el).forEach(function (p) { p.hidden = p.getAttribute('data-tv-panel') !== s.tab; });
    if (this.tab === 'pal') this.drawPal();
    if (this.tab === 'fov') this.drawFov();
    if (this.tab === 'zoom') this.drawZoom();
  };
  Demo.prototype.native = function () {
    var s = this;
    if (!this._native) this._native = load(this.src.scene).then(function (im) {
      // crop the scene to the sensor's aspect ratio first, so 4:3 and 5:4 sensors are not stretched
      var iw = im.naturalWidth, ih = im.naturalHeight, ar = s.dev.w / s.dev.h, cw = Math.min(iw, ih * ar), ch = cw / ar;
      var c = canvas(Math.round(cw), Math.round(ch)); c.getContext('2d').drawImage(im, (iw - cw) / 2, (ih - ch) / 2, cw, ch, 0, 0, c.width, c.height);
      var o = shrink(c, s.dev.w, s.dev.h); grain(o.getContext('2d'), s.dev); return o;
    });
    return this._native;
  };
  Demo.prototype.drawPal = function () {
    var s = this, out = this.el.querySelector('[data-tv-out="pal"]');
    load(this.src.scene).then(function (im) {
      var r = out.getBoundingClientRect(), hi = shrink(im, Math.round(r.width * 2), Math.round(r.width * 2 * im.naturalHeight / im.naturalWidth));
      present(out, hi, s.pal);
    });
  };
  Demo.prototype.drawFov = function () {
    var s = this, el = this.el, a = ANIMALS[this.animal], out = el.querySelector('[data-tv-out="fov"]');
    var ph = el.querySelector('[data-tv-ph]'), o = optics(this.dev), view = o.ifov * this.dev.h * this.dist / this.fz;
    labels(el, this, this.dev);
    load(this.src[this.animal]).then(function (img) {
      ph.hidden = true; out.parentNode.classList.remove('is-ph');
      var f = frameFor(s.dev, img, a.bh || a.h, s.dist);
      scope(out, f, s.dev, s.fz, s.pal);
      s.readFov(o.px(a.h, s.dist), view); // verdicts count pixels across the shoulder (h), not the drawn silhouette (bh)
      // distance strip: same animal, every distance, enlarged to the same tile size
      $$('[data-tv-strip] canvas', el).forEach(function (c, i) {
        var g = frameFor(s.dev, img, a.bh || a.h, DISTS[i]), sd = Math.max(24, Math.round(g.box[3] * 1.4));
        present(c, screen(g.c, s.dev, g.box[0] + g.box[2] / 2 - sd / 2, g.box[1] + g.box[3] / 2 - sd / 2, sd, sd), s.pal);
        c.parentNode.querySelector('em').textContent = verdict(o.px(a.h, DISTS[i]), a.name.toLowerCase());
        c.parentNode.classList.toggle('on', near(DISTS[i], s.dist));
      });
    }, function () {
      // no licensed still for this animal yet
      ph.hidden = false; ph.textContent = "Thermal preview didn't load. Refresh the page to try again."; out.parentNode.classList.add('is-ph');
      var c0 = out.getContext('2d'); c0.clearRect(0, 0, out.width, out.height);
      s.readFov(o.px(a.h, s.dist), view);
      $$('[data-tv-strip] canvas', el).forEach(function (c, i) {
        var g = o.px(a.h, DISTS[i]), x = c.getContext('2d'); x.clearRect(0, 0, c.width, c.height);
        c.parentNode.querySelector('em').textContent = verdict(g, a.name.toLowerCase());
        c.parentNode.classList.toggle('on', near(DISTS[i], s.dist));
      });
    });
  };
  Demo.prototype.readFov = function (px, span) {
    var el = this.el, a = ANIMALS[this.animal];
    el.querySelector('[data-tv-r="span"]').textContent = fmt(span) + ' m';
    el.querySelector('[data-tv-r="mag"]').textContent = magText(this.dev, this.fz);
    el.querySelector('[data-tv-r="verdict"]').textContent = verdict(px, a.name.toLowerCase());
  };
  Demo.prototype.drawZoom = function () {
    var s = this, el = this.el, out = el.querySelector('[data-tv-out="zoom"]');
    this.native().then(function (n) {
      function win(z) { var w = s.dev.w / z, h = s.dev.h / z; return [Math.min(s.dev.w - w, Math.max(0, s.dev.w * ZOOM_AT[0] - w / 2)), Math.min(s.dev.h - h, Math.max(0, s.dev.h * ZOOM_AT[1] - h / 2)), w, h]; }
      var w = win(s.zoom); present(out, screen(n, s.dev, w[0], w[1], w[2], w[3]), s.pal);
      el.querySelector('[data-tv-r="zx"]').textContent = magText(s.dev, s.zoom);
      el.querySelector('[data-tv-r="zq"]').textContent = quality(s.zoom);
      $$('[data-tv-zstrip] figure', el).forEach(function (fg, i) {
        var z = ZOOMS[i], c = fg.querySelector('canvas'), v = win(z);
        fg.hidden = z > zmax(s.dev) * 1.01;
        present(c, screen(n, s.dev, v[0], v[1], v[2], v[3]), s.pal);
        fg.querySelector('[data-tv-zl]').textContent = magText(s.dev, z);
        fg.classList.toggle('on', z === s.zoom);
      });
    });
  };

  // ================= side by side compare =================
  function Compare(el) {
    this.el = el;
    var data = JSON.parse(el.querySelector('[data-tvc-devs]').textContent);
    this.devs = []; this.byH = {}; this.add(data.devices);
    this.src = JSON.parse(el.getAttribute('data-src'));
    this.pal = 'whitehot'; this.animal = 'pig'; this.dist = 200; this.fz = 1;
    var s = this;
    this.sel = $$('[data-tvc-pick]', el);
    this.fill(this.defaults());
    this.sel.forEach(function (sl) { sl.addEventListener('change', function () { s.draw(); }); });
    $$('[data-tv-pal]', el).forEach(function (b) { b.addEventListener('click', function () { s.pal = b.getAttribute('data-tv-pal'); s.draw(); }); });
    $$('[data-tv-animal]', el).forEach(function (b) { b.addEventListener('click', function () { s.animal = b.getAttribute('data-tv-animal'); s.draw(); }); });
    sliders(el, s);
    var raf; window.addEventListener('resize', function () { cancelAnimationFrame(raf); raf = requestAnimationFrame(function () { s.draw(); }); });
    this.draw();
    if (data.pages > 1) this.more(data);
  }
  Compare.prototype.add = function (list) {
    var s = this;
    list.forEach(function (d) { if (!s.byH[d.h]) { s.byH[d.h] = d; s.devs.push(d); } });
    this.devs.sort(function (a, b) { return a.t.localeCompare(b.t); });
  };
  // Collections over 250 products: fetch the remaining pages as JSON, then refresh the pickers.
  Compare.prototype.more = function (data) {
    var s = this, pv = new URLSearchParams(location.search).get('preview_theme_id'), jobs = [];
    for (var i = 2; i <= data.pages; i++) {
      jobs.push(fetch(data.url + '?view=thermal-data&page=' + i + (pv ? '&preview_theme_id=' + pv : ''), { credentials: 'same-origin' })
        .then(function (r) { return r.json(); }).then(function (j) { return j.devices; }, function () { return []; }));
    }
    Promise.all(jobs).then(function (pages) {
      var before = s.devs.length, cur = s.sel.map(function (sl) { return sl.value; });
      pages.forEach(function (l) { s.add(l); });
      if (s.devs.length === before) return;
      var want = (new URLSearchParams(location.search).get('devices') || '').split(',').filter(Boolean);
      var missing = want.some(function (h) { return cur.indexOf(h) < 0 && s.byH[h]; });
      s.fill(missing ? s.defaults() : cur); s.draw();
    });
  };
  Compare.prototype.fill = function (pick) {
    var s = this, opts = this.devs.map(function (d) { return '<option value="' + d.h + '">' + d.t.replace(/</g, '&lt;') + '</option>'; }).join('');
    this.sel.forEach(function (sl, i) {
      sl.innerHTML = (i > 1 ? '<option value="">None</option>' : '') + opts;
      sl.value = pick[i] && s.byH[pick[i]] ? pick[i] : '';
    });
  };
  // Default picks: ?devices= first, then the same sensor on a short and a long lens, then a different sensor,
  // so the field of view trade-off is visible straight away. Returns handles.
  Compare.prototype.defaults = function () {
    var d = this.devs, s = this, q = new URLSearchParams(location.search).get('devices'), out = [];
    if (q) q.split(',').forEach(function (h) { if (s.byH[h] && out.indexOf(h) < 0) out.push(h); });
    if (out.length >= 2) return out.slice(0, 3);
    var s640 = d.filter(function (x) { return x.w === 640 && out.indexOf(x.h) < 0; }).sort(function (a, b) { return a.f - b.f; });
    if (!out.length && s640.length > 1) out = [s640[0].h, s640[s640.length - 1].h];
    else if (out.length && s640.length) {
      var me = s.byH[out[0]], far = s640.filter(function (x) { return x.f !== me.f; });
      if (far.length) out.push((me.f <= 35 ? far[far.length - 1] : far[0]).h);
    }
    var other = d.filter(function (x) { return (x.w === 1280 || x.w === 384) && out.indexOf(x.h) < 0; });
    if (other.length && out.length < 3) out.push(other[0].h);
    for (var k = 0; out.length < 3 && k < d.length; k++) if (out.indexOf(d[k].h) < 0) out.push(d[k].h);
    return out;
  };
  Compare.prototype.draw = function () {
    var s = this, el = this.el, a = ANIMALS[this.animal];
    $$('[data-tv-pal]', el).forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-tv-pal') === s.pal)); });
    $$('[data-tv-animal]', el).forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-tv-animal') === s.animal)); });
    labels(el, this);
    var img = load(this.src[this.animal]);
    var ph = el.querySelector('[data-tv-ph]');
    $$('[data-tvc-col]', el).forEach(function (col, i) {
      var dev = s.byH[s.sel[i].value] || null;
      col.hidden = !dev;
      if (!dev) return;
      var dv = { w: dev.w, h: dev.h2, pitch: dev.p, focal: dev.f, ret: dev.k === 'scope', m: dev.m, mx: dev.mx, dw: dev.dw, dh: dev.dh };
      var o = optics(dv), px = o.px(a.h, s.dist), z = Math.min(s.fz, zmax(dv)), nm = a.name.toLowerCase();
      col.querySelector('[data-r="spec"]').textContent = dev.w + '×' + dev.h2 + ' (' + mp(dev.w * dev.h2) + ') · ' + dev.f + ' mm · ' + (dev.dh ? dev.dw + '×' + dev.dh : 'not listed');
      col.querySelector('[data-r="fov"]').textContent = o.hfov.toFixed(1) + '° × ' + o.vfov.toFixed(1) + '°';
      col.querySelector('[data-r="span"]').textContent = fmt(o.ifov * dv.h * s.dist / z) + ' m';
      col.querySelector('[data-r="mag"]').textContent = magText(dv, z) + (z < s.fz ? ' (max)' : '');
      col.querySelector('[data-r="px"]').textContent = verdict(px, nm);
      col.querySelector('[data-r="rng"]').textContent = JOHNSON.slice().reverse().map(function (j) { return j.to(nm) + ' ' + fmt(o.range(a.h, j.px)) + ' m'; }).join(' · ');
      var link = col.querySelector('[data-r="link"]'); link.href = '/products/' + dev.h; link.textContent = dev.t;
      img.then(function (im) {
        ph.hidden = true;
        var f = frameFor(dv, im, a.bh || a.h, s.dist);
        scope(col.querySelector('[data-out="frame"]'), f, dv, z, s.pal);
      }, function () { ph.hidden = false; ph.textContent = "Thermal preview didn't load. Refresh the page to try again."; var c = col.querySelector('[data-out="frame"]'); c.getContext('2d').clearRect(0, 0, c.width, c.height); });
    });
  };

  function init() {
    $$('[data-tv]').forEach(function (el) { if (!el._tv) el._tv = new Demo(el); });
    $$('[data-tvc]').forEach(function (el) { if (!el._tv) el._tv = new Compare(el); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
